
import React from 'react';
import { SectionCard } from '@/components/common/SectionCard';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { TrendingUp, BarChart2, Store, Truck, Filter, BarChartHorizontal } from 'lucide-react';
import {
    LineChartSkeleton, BarChartSkeleton, HorizontalBarChartSkeleton,
    DonutChartSkeleton, FunnelChartSkeleton
} from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';

interface ChartPoint { label: string; revenue: number; orders: number; }
interface StorePoint { storeName: string; revenue: number; ordersToday: number; }
interface DeliveryPoint { name: string; value: number; color: string; }
interface FunnelStep { stage: string; count: number; }
interface HeatCell { day: string; hour: number; value: number; }

interface Props {
    revenueData: ChartPoint[];
    storeData: StorePoint[];
    deliveryData: DeliveryPoint[];
    funnelData: FunnelStep[];
    loading?: boolean;
}

export const BusinessChartsSection: React.FC<Props> = ({
    revenueData, storeData, deliveryData, funnelData, loading = false
}) => {
    return (
        <div className="space-y-6">
            {/* Row 1: Revenue Trend + Order Volume Trend */}
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                <SectionCard title="Revenue Trend" icon={<TrendingUp />}>
                    {loading ? (
                        <LineChartSkeleton />
                    ) : revenueData.length === 0 ? (
                        <EmptyChart />
                    ) : (
                        <div className="h-64 pt-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={revenueData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'currentColor', opacity: 0.5, fontSize: 11 }} dy={8} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'currentColor', opacity: 0.5, fontSize: 11 }} tickFormatter={v => `R${v}`} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                                        formatter={(v: any) => [`R${v}`, 'Revenue']}
                                    />
                                    <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </SectionCard>

                <SectionCard title="Order Volume Trend" icon={<BarChart2 />}>
                    {loading ? (
                        <BarChartSkeleton />
                    ) : revenueData.length === 0 ? (
                        <EmptyChart />
                    ) : (
                        <div className="h-64 pt-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={revenueData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'currentColor', opacity: 0.5, fontSize: 11 }} dy={8} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'currentColor', opacity: 0.5, fontSize: 11 }} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                                        cursor={{ fill: 'hsl(var(--secondary))' }}
                                    />
                                    <Bar dataKey="orders" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </SectionCard>
            </div>

            {/* Row 2: Top Performing Stores + Delivery Donut + Order Funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <SectionCard title="Top Performing Stores" icon={<Store />}>
                    {loading ? (
                        <HorizontalBarChartSkeleton />
                    ) : storeData.length === 0 ? (
                        <EmptyChart />
                    ) : (
                        <div className="h-64 pt-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart layout="vertical" data={storeData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: 'currentColor', opacity: 0.5, fontSize: 11 }} tickFormatter={v => `R${v}`} />
                                    <YAxis type="category" dataKey="storeName" axisLine={false} tickLine={false} tick={{ fill: 'currentColor', opacity: 0.7, fontSize: 11 }} width={80} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                                        formatter={(v: any) => [`R${v}`, 'Revenue']}
                                    />
                                    <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </SectionCard>

                <SectionCard title="Delivery Success Rate" icon={<Truck />}>
                    {loading ? (
                        <DonutChartSkeleton />
                    ) : deliveryData.length === 0 ? (
                        <EmptyChart />
                    ) : (
                        <div className="h-64 flex items-center justify-center">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={deliveryData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                                        {deliveryData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                                        formatter={(v: any, name: any) => [`${v}%`, name]}
                                    />
                                    <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </SectionCard>

                <SectionCard title="Order Status Funnel" icon={<Filter />}>
                    {loading ? (
                        <FunnelChartSkeleton />
                    ) : funnelData.length === 0 || funnelData.every(s => s.count === 0) ? (
                        <EmptyChart />
                    ) : (
                        <div className="h-64 pt-4 space-y-2 overflow-hidden">
                            {funnelData.map((step, i) => {
                                const maxCount = funnelData[0]?.count || 1;
                                const width = Math.round((step.count / maxCount) * 100);
                                const colors = ['#6366F1', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'];
                                return (
                                    <div key={i} className="flex items-center gap-2">
                                        <span className="text-[10px] text-muted-foreground w-20 shrink-0 text-right">{step.stage}</span>
                                        <div className="flex-1 h-7 bg-secondary/20 rounded overflow-hidden">
                                            <div
                                                className="h-full rounded flex items-center px-2 transition-all duration-700"
                                                style={{ width: `${Math.max(width, 8)}%`, backgroundColor: colors[i] || '#6366F1' }}
                                            >
                                                <span className="text-white text-[10px] font-bold">{step.count}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </SectionCard>
            </div>
        </div>
    );
};
